import {
  AvailableTasksSectionRead,
  AvailableTasksThemeRead,
  useApiV1UserPreferencesRetrieveQuery,
} from "@/services/generatedApi";
import { AssessmentUserPreference, Menu } from "@/ui-components/layouts/components/AssessmentsMenu/types";

export const NO_STANDARD_ID = "-1";
export const PREFERENCE_NAME = "available_tasks";
export const makePreferenceKeyForAvailableTasks = (assessmentId: string, siteId: string) =>
  `${PREFERENCE_NAME}.${assessmentId}.${siteId}`;
export const groupIsActive = (group: Menu, expanded: string[] | undefined) =>
  expanded && group.sub_groups.some(sub_group => sub_group.tasks.some(({ id }) => expanded?.includes(id)));

export const adaptAvailableTasksThemeReadIntoMenu = (theme: AvailableTasksThemeRead): Menu => ({
  group_id: theme.theme_id,
  group_name: theme.theme_name,
  sub_groups: theme.sub_themes.map(sub_theme => ({
    sub_group_id: sub_theme.sub_theme_id,
    sub_group_name: sub_theme.sub_theme_name,
    tasks: [...sub_theme.tasks],
  })),
});

export const adaptAvailableTasksSectionReadIntoMenu = (section: AvailableTasksSectionRead): Menu => ({
  group_id: section.section_ref,
  group_name: `${section.section_ref} ${section.section_name}`.trim(),
  sub_groups: section.sub_sections.map(sub_section => ({
    sub_group_id: sub_section.sub_section_ref,
    sub_group_name: sub_section.sub_section_ref,
    tasks: [...sub_section.tasks],
  })),
});

export const DEFAULT_ASSESSMENT_PREFERENCE: AssessmentUserPreference = {
  standard: NO_STANDARD_ID,
  filters: {},
};

export const useAssessmentPreferences = (
  assessmentId: string,
  siteAssessmentId: string,
): {
  isLoading: boolean;
  tableName: string;
  preference: AssessmentUserPreference;
} => {
  const tableName = makePreferenceKeyForAvailableTasks(assessmentId, siteAssessmentId);
  const { data, isLoading } = useApiV1UserPreferencesRetrieveQuery({ tableName });
  if (isLoading) {
    return { tableName, isLoading, preference: DEFAULT_ASSESSMENT_PREFERENCE };
  }

  let preference: AssessmentUserPreference = DEFAULT_ASSESSMENT_PREFERENCE;
  const storedPreference = data?.preference;
  // This is required for backward compatibility
  // as previously the remaining filters were not saved in the preferences
  // just the standard as a string or numeric value;
  if (storedPreference) {
    // Previously we incorrectly assumed that the standard was always a string
    // due to StandardSelect having incorrect type, it was actually passing a number
    // so we need to check if it's a number and convert it to string for new compatability
    if (typeof storedPreference === "number" || typeof storedPreference === "string") {
      preference = { ...preference, standard: String(storedPreference) };
    } else {
      preference = storedPreference as AssessmentUserPreference;
    }
  }

  return { tableName, isLoading, preference };
};
